import { CHAT_FUNCTIONS } from '../../../config/chatFunctionNames';

/**
 * Detect community announcement messages.
 *
 * Live (just-arrived) messages carry function_name. History rows loaded
 * from the database often have function_name === null but still preserve
 * `intent` and the full `raw_output.ca_card`. SMS-channel rows are the
 * same. Matching any of the three signals keeps the card working in
 * every case (fresh, reload, SMS-mirror).
 *
 * Three shapes exist:
 *   - Author preview: raw_output.ca_card.type === 'ca_author_preview'
 *   - Director approval: raw_output.ca_card.type === 'ca_director_approval'
 *   - Queued / status (no ca_card) — handled as plain text, not a card.
 */
export function isCommunityAnnouncementMessage(message) {
  if (!message) return false;
  const target = CHAT_FUNCTIONS.COMMUNITY_ANNOUNCEMENT;
  return (
    message.function_name === target ||
    message.intent === target ||
    message.raw_output?.type === target
  );
}

/**
 * Extract structured data for the CA card. Returns null when the
 * message has no ca_card payload (case C — plain status text).
 *
 * @returns {{
 *   variant: 'preview' | 'approval',
 *   header: string,
 *   body: string,
 *   footer: string,
 *   images: string[],
 *   caId: number | string | null,
 * } | null}
 */
export function getCommunityAnnouncementData(message) {
  if (!isCommunityAnnouncementMessage(message)) return null;

  const card = message?.raw_output?.ca_card;
  if (!card || typeof card !== 'object') return null;

  const variant = card.type === 'ca_director_approval' ? 'approval' : 'preview';

  const rawImages = Array.isArray(card.image_urls) ? card.image_urls : [];
  const images = rawImages
    .filter(url => typeof url === 'string' && url.trim().length > 0);

  return {
    variant,
    header: card.header || '',
    body: card.body || '',
    footer: card.footer || '',
    images,
    caId: card.ca_id ?? null,
  };
}

/**
 * Header text the typewriter should show above the card.
 * Returns null when there's no card (case C → caller uses default content).
 */
export function getCommunityAnnouncementHeader(message) {
  const data = getCommunityAnnouncementData(message);
  return data ? data.header : null;
}
